import { useState, useEffect, useCallback } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';
import Swal from 'sweetalert2';

const useAuth = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verificar autenticación al cargar
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Verificar estado de autenticación
  const checkAuthStatus = useCallback(() => {
    try {
      const authenticated = tokenManager.isAuthenticated();
      const userData = tokenManager.getUserData();
      
      setIsAuthenticated(authenticated);
      setUser(userData);
      setLoading(false);
    } catch (error) {
      console.error('Error checking auth status:', error);
      setError('Error al verificar autenticación');
      setLoading(false);
    }
  }, []);

  // Función de login
  const login = async (credentials) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CIFRA.usuario.login, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error en la autenticación');
      }

      // Guardar tokens y datos del usuario
      tokenManager.setTokens(
        data.tokens.access,
        data.tokens.refresh,
        data.usuario
      );

      setUser(data.usuario);
      setIsAuthenticated(true);
      setError(null);

      return { success: true, user: data.usuario };

    } catch (error) {
      // Sin modal — el login de la consola (único consumidor de `login()`,
      // el ciudadano no tiene cuenta) muestra el aviso inline con `error`,
      // siguiendo el diseño (design_handoff_reportes_ciudadanos, sección
      // 7.1: aviso ámbar bajo los campos, no un modal encima del form).
      //
      // `fetch()` lanza un `TypeError` genérico ("Failed to fetch",
      // "NetworkError...", "Load failed" según el navegador) cuando ni
      // siquiera pudo conectar con el servidor — antes ese texto técnico en
      // inglés se mostraba tal cual. Se traduce acá; cualquier otro error
      // (ej. credenciales incorrectas) ya viene en español desde el backend.
      const mensaje = error instanceof TypeError
        ? 'No se pudo conectar con el servidor. Verificá tu conexión e intentá de nuevo.'
        : error.message;
      setError(mensaje);
      setIsAuthenticated(false);
      setUser(null);

      return { success: false, error: mensaje };
    } finally {
      setLoading(false);
    }
  };

  // Función de registro
  const register = async (userData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CIFRA.usuario.registro, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error en el registro');
      }

      // Mostrar mensaje de éxito
      await Swal.fire({
        icon: 'success',
        title: 'Registro Exitoso',
        text: 'Usuario registrado correctamente. Puedes iniciar sesión.',
      });

      return { success: true, user: data };

    } catch (error) {
      setError(error.message);

      // Mostrar error
      await Swal.fire({
        icon: 'error',
        title: 'Error en el Registro',
        text: error.message,
      });

      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Cierre de sesión — pide confirmación primero (el personal del panel
  // puede tener cambios sin guardar en un ticket/reporte abierto) y solo
  // limpia tokens si la confirma. `cancelado: true` le dice al que llamó
  // que no pasó nada, sin tratarlo como un error real.
  const logout = async () => {
    const confirmacion = await Swal.fire({
      icon: 'question',
      title: '¿Cerrar sesión?',
      text: 'Vas a salir de la consola de operaciones.',
      showCancelButton: true,
      confirmButtonText: 'Sí, cerrar sesión',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1B5FA8',
    });

    if (!confirmacion.isConfirmed) {
      return { success: false, cancelado: true };
    }

    try {
      tokenManager.clearTokens();
      setUser(null);
      setIsAuthenticated(false);
      setError(null);

      await Swal.fire({
        icon: 'success',
        title: 'Sesión cerrada',
        timer: 1500,
        showConfirmButton: false,
      });

      return { success: true };

    } catch (error) {
      console.error('Error en logout:', error);
      return { success: false, error: error.message };
    }
  };

  // Función para obtener perfil actualizado
  const getProfile = async () => {
    if (!isAuthenticated) {
      return { success: false, error: 'No autenticado' };
    }

    setLoading(true);
    setError(null);

    try {
      const token = tokenManager.getAccessToken();
      const response = await fetch(API_CIFRA.usuario.perfil, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al obtener perfil');
      }

      // Actualizar datos del usuario
      setUser(data);
      tokenManager.setTokens(
        tokenManager.getAccessToken(),
        tokenManager.getRefreshToken(),
        data
      );

      return { success: true, user: data };

    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Función para actualizar perfil
  const updateProfile = async (userData) => {
    if (!isAuthenticated) {
      return { success: false, error: 'No autenticado' };
    }

    setLoading(true);
    setError(null);

    try {
      const token = tokenManager.getAccessToken();
      const response = await fetch(API_CIFRA.usuario.detalle(user.usu_inId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al actualizar perfil');
      }

      // Actualizar datos del usuario
      setUser(data);
      tokenManager.setTokens(
        tokenManager.getAccessToken(),
        tokenManager.getRefreshToken(),
        data
      );

      // Mostrar mensaje de éxito
      await Swal.fire({
        icon: 'success',
        title: 'Perfil Actualizado',
        text: 'Tu perfil se actualizó correctamente.',
      });

      return { success: true, user: data };

    } catch (error) {
      setError(error.message);

      // Mostrar error
      await Swal.fire({
        icon: 'error',
        title: 'Error al Actualizar',
        text: error.message,
      });

      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Cambiar la contraseña propia (HU-013) — autoservicio, separado a
  // propósito de `updateProfile`/`detalle_usuario` (edición de datos): acá
  // el backend exige la contraseña actual, no solo tener sesión abierta.
  // Sin modal de éxito/error — el que llama (`ModalCambiarPassword.jsx`)
  // ya maneja su propio mensaje inline, para no duplicar feedback.
  const cambiarMiPassword = async (passwordActual, passwordNueva) => {
    try {
      const token = tokenManager.getAccessToken();
      const response = await fetch(API_CIFRA.usuario.cambiarMiPassword, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password_actual: passwordActual, password_nueva: passwordNueva }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo cambiar la contraseña');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Función para refresh token manual
  const refreshToken = async () => {
    try {
      const refreshTokenValue = tokenManager.getRefreshToken();
      
      if (!refreshTokenValue) {
        throw new Error('No hay refresh token disponible');
      }

      const response = await fetch(API_CIFRA.usuario.refreshToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshTokenValue }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error('Error al renovar token');
      }

      // Actualizar access token
      tokenManager.updateAccessToken(data.access);

      return { success: true, accessToken: data.access };

    } catch (error) {
      console.error('Error refreshing token:', error);
      // Si falla el refresh, hacer logout
      await logout();
      return { success: false, error: error.message };
    }
  };

  return {
    // Estado
    user,
    isAuthenticated,
    loading,
    error,
    
    // Funciones
    login,
    register,
    logout,
    getProfile,
    updateProfile,
    cambiarMiPassword,
    refreshToken,
    checkAuthStatus,
  };
};

export default useAuth; 